import { Icon } from '@components/gassan-ui'
import { cn } from '@lib/cn'
import NextLink from 'next/link'
import { AnchorHTMLAttributes, ButtonHTMLAttributes, ForwardedRef, forwardRef } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'dark' | 'light'

interface ButtonProps {
  variant: ButtonVariant
  iconRight?: 'chevron-right'
}

export type ButtonType = ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonProps & {
    as: 'button'
    status?: 'idle' | 'disabled'
    disabled?: boolean
  }

export type ButtonLinkType = AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonProps & {
    href: string
    status?: never
    as: 'link'
  }

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonType | ButtonLinkType
>(
  (
    {
      as: Component = 'button',
      variant = 'black',
      iconRight,
      children,
      className = '',
      status,
      ...rest
    },
    ref,
  ) => {
    const finalClassName = cn(
      'group w-auto flex h-[44px] items-center border rounded-full border-solid text-[14px] leading-[1.7] font-bold border-transparent px-8 transition-colors duration-100',
      'focus:outline-none',
      variant === 'primary' &&
        'bg-rolexGreenAlt text-white hover:border-rolexPrimary hover:bg-rolexPrimary hover:text-white focus:ring-2 focus:ring-rolexOceanGreen',
      variant === 'secondary' &&
        'bg-transparent text-rolexGreenAlt hover:text-rolexBlack focus:ring-2 focus:ring-rolexGreenAlt',
      'disabled:pointer-events-none disabled:opacity-50',
      variant === 'dark' &&
        'bg-shade-700 text-white hover:border-rolexPrimary hover:bg-rolexPrimary hover:text-white focus:ring-2 focus:ring-rolexOceanGreen',
      variant === 'light' &&
        'bg-rolexGrey bg-opacity-30 hover:bg-opacity-[0.15] text-rolexBrown disabled:border-rolexShade-200 disabled:bg-rolexShade-200 disabled:text-rolexShade-700 focus:ring-2 focus:ring-rolexGreenAlt',
      className,
    )

    if (Component === 'link') {
      const props = rest as Omit<ButtonLinkType, 'as'>
      const { href } = props
      return (
        <NextLink
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          href={href}
          className={finalClassName}
          {...props}
        >
          <InnerButton />
        </NextLink>
      )
    }

    return (
      <Component
        ref={ref as ForwardedRef<HTMLButtonElement>}
        className={finalClassName}
        disabled={status === 'disabled'}
        {...(rest as Omit<ButtonType, 'as'>)}
      >
        <InnerButton />
      </Component>
    )

    function InnerButton() {
      return (
        <>
          {children}
          {iconRight === 'chevron-right' && (
            <Icon
              icon="chevron-right"
              size={28}
              className="transition-transform group-hover:translate-x-1"
            />
          )}
        </>
      )
    }
  },
)

Button.displayName = 'Button'
